import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '../shared/Toast';

// These are system categories — locked (cannot be deleted)
const SYSTEM_IDS = new Set([
  'sal','free','inv','bonus','other-in',
  'moradia','alim','transp','saude','educ',
  'lazer','contas','vest','pets','cartao','other-ex',
]);

const ICON_OPTIONS = [
  'fa-house','fa-utensils','fa-car','fa-heart-pulse','fa-graduation-cap',
  'fa-gamepad','fa-file-invoice','fa-shirt','fa-paw','fa-credit-card',
  'fa-laptop','fa-building-columns','fa-chart-line','fa-gift','fa-circle-plus',
  'fa-shopping-bag','fa-dumbbell','fa-plane','fa-baby','fa-music',
];

const COLOR_OPTIONS = [
  '#e24b4a','var(--gold)','#1d9e75','#185FA5','#378ADD',
  '#993556','#854F0B','#3B6D11','#A32D2D','#533F91',
  '#0F6E56','#888780','#0d1b2a','#6366f1','#f97316',
];

function CategoryModal({ cat, onClose, onSave, defaultType = 'expense' }) {
  const [form, setForm] = useState(
    cat ? { name: cat.name, icon: cat.icon, color: cat.color, type: cat.type }
        : { name: '', icon: 'fa-circle-plus', color: '#378ADD', type: defaultType }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.name.trim()) { toast.error('Digite um nome para a categoria.'); return; }
    onSave(form);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          {cat ? 'Editar categoria' : 'Nova categoria'}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {!cat && (
          <div style={{ marginBottom: 16 }}>
            <label className="form-label" style={{ marginBottom: 6 }}>Tipo de lançamento</label>
            <div className="type-toggle">
              <button className={`type-btn ${form.type === 'income' ? 'active-income' : ''}`} onClick={() => set('type', 'income')}>
                <i className="fas fa-arrow-up"></i> Receita
              </button>
              <button className={`type-btn ${form.type === 'expense' ? 'active-expense' : ''}`} onClick={() => set('type', 'expense')}>
                <i className="fas fa-arrow-down"></i> Despesa
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
              <i className="fas fa-circle-info" style={{ marginRight: 4 }}></i>
              Esta categoria aparecerá nos lançamentos de <strong>{form.type === 'expense' ? 'Despesa' : 'Receita'}</strong>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Nome da categoria *</label>
          <input className="form-control" autoFocus value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Lazer" />
        </div>

        <div className="form-group">
          <label className="form-label">Ícone</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ICON_OPTIONS.map(ic => (
              <div key={ic} onClick={() => set('icon', ic)} style={{
                width: 34, height: 34, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                background: form.icon === ic ? form.color : 'var(--surface2)',
                border: form.icon === ic ? `2px solid ${form.color}` : '2px solid transparent',
                color: form.icon === ic ? '#fff' : 'var(--muted)',
                fontSize: 13, transition: 'all 0.12s',
              }}>
                <i className={`fas ${ic}`}></i>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Cor</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COLOR_OPTIONS.map(c => (
              <div key={c} onClick={() => set('color', c)} style={{
                width: 28, height: 28, borderRadius: '50%',
                background: c, cursor: 'pointer',
                border: form.color === c ? '3px solid var(--text)' : '2px solid transparent',
                outline: form.color === c ? `2px solid ${c}` : 'none',
                outlineOffset: 2,
                transition: 'transform 0.12s',
              }} />
            ))}
          </div>
        </div>

        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Salvar
        </button>
      </div>
    </div>
  );
}

export default function Categories() {
  const { state, dispatch } = useApp();
  const [tab, setTab] = useState('expense');
  const [modal, setModal] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [addingSubFor, setAddingSubFor] = useState(null);
  const [subInput, setSubInput] = useState('');
  const [editingSub, setEditingSub] = useState(null); // { catId, idx }
  const [editingSubValue, setEditingSubValue] = useState('');
  const [search, setSearch] = useState('');

  const catsAll = state.categories.filter(c => c.type === tab);
  const q = search.trim().toLowerCase();
  const cats = q
    ? catsAll.filter(c => c.name.toLowerCase().includes(q) || (c.subcategories || []).some(s => s.toLowerCase().includes(q)))
    : catsAll;

  const openAddSub = (catId) => {
    setAddingSubFor(catId);
    setSubInput('');
  };

  const confirmAddSub = (cat) => {
    const name = subInput.trim();
    if (!name) return;
    const subs = cat.subcategories || [];
    if (subs.includes(name)) { toast.error('Subcategoria já existe.'); return; }
    dispatch({ type: 'UPDATE_CATEGORY', payload: { ...cat, subcategories: [...subs, name] } });
    toast.success(`"${name}" adicionada!`);
    setAddingSubFor(null);
    setSubInput('');
  };

  const deleteSub = (cat, subName) => {
    const subs = (cat.subcategories || []).filter(s => s !== subName);
    dispatch({ type: 'UPDATE_CATEGORY', payload: { ...cat, subcategories: subs } });
    toast.info(`"${subName}" removida.`);
  };

  const openEditSub = (catId, idx, currentName) => {
    setEditingSub({ catId, idx });
    setEditingSubValue(currentName);
    setAddingSubFor(null);
  };

  const confirmEditSub = (cat, idx) => {
    const name = editingSubValue.trim();
    if (!name) return;
    const subs = [...(cat.subcategories || [])];
    if (subs.some((s, i) => s === name && i !== idx)) { toast.error('Subcategoria já existe.'); return; }
    subs[idx] = name;
    dispatch({ type: 'UPDATE_CATEGORY', payload: { ...cat, subcategories: subs } });
    toast.success(`Subcategoria atualizada!`);
    setEditingSub(null);
  };

  const deleteCat = (cat) => {
    if (SYSTEM_IDS.has(cat.id)) return;
    if (window.confirm(`Excluir a categoria "${cat.name}"?`)) {
      dispatch({ type: 'DELETE_CATEGORY', payload: cat.id });
      toast.info('Categoria excluída.');
    }
  };

  const handleSave = (form) => {
    if (editCat) {
      dispatch({ type: 'UPDATE_CATEGORY', payload: { ...editCat, ...form } });
      toast.success('Categoria atualizada!');
    } else {
      dispatch({ type: 'ADD_CATEGORY', payload: { id: uuidv4(), subcategories: [], ...form } });
      toast.success('Categoria criada!');
    }
  };

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Categorias</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Organize suas categorias e subcategorias</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditCat(null); setModal('cat'); }}>
          <i className="fas fa-plus"></i> Nova {tab === 'expense' ? 'Despesa' : 'Receita'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 10, padding: 4, gap: 4, marginBottom: 20, width: 'fit-content' }}>
        {[{ id: 'expense', label: 'Despesas' }, { id: 'income', label: 'Receitas' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 22px', border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? 'var(--card-bg)' : 'transparent',
            color: tab === t.id ? 'var(--text)' : 'var(--muted)',
            boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Categorias</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', marginTop: 4 }}>{catsAll.length}</div>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Subcategorias</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', marginTop: 4 }}>
            {catsAll.reduce((s, c) => s + (c.subcategories?.length || 0), 0)}
          </div>
        </div>
      </div>

      {/* Busca */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <i className="fas fa-magnifying-glass" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13 }}></i>
        <input
          className="form-control"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Pesquisar por categoria ou subcategoria..."
          style={{ paddingLeft: 38 }}
        />
      </div>

      {/* Category grid */}
      <div className="cat-grid">
        {cats.map(cat => {
          const subs = cat.subcategories || [];
          const isSystem = SYSTEM_IDS.has(cat.id);
          const color = cat.color || '#888';

          return (
            <div key={cat.id} className="cat-card">
              {/* Header */}
              <div className="cat-card-header">
                <div className="cat-card-icon" style={{ background: `${color}18`, color }}>
                  <i className={`fas ${cat.icon || 'fa-tag'}`}></i>
                </div>
                <span className="cat-card-name">
                  {cat.name}
                  {isSystem && <i className="fas fa-lock" title="Categoria do sistema" style={{ marginLeft: 6, fontSize: 9, color: 'var(--muted)' }}></i>}
                </span>
                <div className="cat-card-actions">
                  <button title="Editar categoria" onClick={() => { setEditCat(cat); setModal('cat'); }}>
                    <i className="fas fa-pen"></i>
                  </button>
                  {!isSystem && (
                    <button title="Excluir categoria" onClick={() => deleteCat(cat)} className="danger">
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                  <button title="Adicionar subcategoria" onClick={() => openAddSub(cat.id)} className="cat-card-add">
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <div className="cat-card-subs-label">Subcategorias</div>

              {/* Chips */}
              <div className="cat-chip-grid">
                {subs.map((sub, idx) => {
                  const isEditing = editingSub?.catId === cat.id && editingSub?.idx === idx;
                  return isEditing ? (
                    <div key={idx} className="cat-chip cat-chip-editing">
                      <input
                        className="form-control"
                        autoFocus
                        value={editingSubValue}
                        onChange={e => setEditingSubValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') confirmEditSub(cat, idx); if (e.key === 'Escape') setEditingSub(null); }}
                        style={{ fontSize: 12, height: 26, flex: 1, minWidth: 0 }}
                      />
                      <button onClick={() => confirmEditSub(cat, idx)}><i className="fas fa-check"></i></button>
                      <button onClick={() => setEditingSub(null)}><i className="fas fa-times"></i></button>
                    </div>
                  ) : (
                    <div key={idx} className="cat-chip" onClick={() => openEditSub(cat.id, idx, sub)}>
                      <span>{sub}</span>
                      <button
                        title="Remover"
                        onClick={(e) => { e.stopPropagation(); deleteSub(cat, sub); }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  );
                })}

                {/* Add subcategory inline */}
                {addingSubFor === cat.id && (
                  <div className="cat-chip cat-chip-editing" style={{ minWidth: 160 }}>
                    <input
                      className="form-control"
                      autoFocus
                      value={subInput}
                      onChange={e => setSubInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') confirmAddSub(cat); if (e.key === 'Escape') setAddingSubFor(null); }}
                      placeholder="Nova subcategoria..."
                      style={{ fontSize: 12, height: 26, flex: 1, minWidth: 0 }}
                    />
                    <button onClick={() => confirmAddSub(cat)}><i className="fas fa-check"></i></button>
                    <button onClick={() => setAddingSubFor(null)}><i className="fas fa-times"></i></button>
                  </div>
                )}

                {subs.length === 0 && addingSubFor !== cat.id && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', padding: '4px 0' }}>Nenhuma subcategoria ainda.</div>
                )}
              </div>
            </div>
          );
        })}

        {cats.length === 0 && (
          <div className="empty-state card" style={{ gridColumn: '1 / -1' }}>
            <i className="fas fa-tag"></i>
            <div>{q ? 'Nenhuma categoria encontrada.' : `Nenhuma categoria de ${tab === 'expense' ? 'despesa' : 'receita'}`}</div>
            {!q && (
              <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={() => { setEditCat(null); setModal('cat'); }}>
                <i className="fas fa-plus"></i> Criar categoria
              </button>
            )}
          </div>
        )}
      </div>

      {modal === 'cat' && (
        <CategoryModal
          cat={editCat}
          defaultType={tab}
          onClose={() => { setModal(null); setEditCat(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
